<script setup>
import IgnorePhoneCallsAction from '@frontJs/components/actions/projectManagement/apikom/IgnorePhoneCallsAction.vue';
import ImportPhoneCallsAction from '@frontJs/components/actions/projectManagement/apikom/ImportPhoneCallsAction.vue';
import InvalidatePhoneCallAction from '@frontJs/components/actions/projectManagement/apikom/InvalidatePhoneCallAction.vue';
import TogglePhoneCallIgnoredAction from '@frontJs/components/actions/projectManagement/apikom/TogglePhoneCallIgnoredAction.vue';
import ValidatePhoneCallAction from '@frontJs/components/actions/projectManagement/apikom/ValidatePhoneCallAction.vue';
import ArrowUUpLeftIcon from '@iconify-vue/ph/arrow-u-up-left';
import ArrowsClockwiseIcon from '@iconify-vue/ph/arrows-clockwise';
import LinkIcon from '@iconify-vue/ph/link';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import ProhibitIcon from '@iconify-vue/ph/prohibit';
import XIcon from '@iconify-vue/ph/x';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate, formatPhoneNumber, formatTime } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastSaved } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    status = null,
    type = null,
    includeMissed = false,
    callback,
} = defineProps({
    status: {
        type: String,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
    includeMissed: {
        type: Boolean,
        required: false,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const typePillTypes = {
    accepted: 'success',
    missed: 'danger',
    outgoing: 'secondary',
};

const statusPillTypes = {
    to_process: 'warning',
    validated: 'success',
    ignored: 'secondary',
};

const {
    loading,
    table,
    globalData,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    selectionIds,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'calledAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Appel du',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text:
                        formatDate(props.row.original.calledAtDate) +
                        ' ' +
                        props.row.original.calledAtTime,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                    width: 0,
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.type.label,
                    type: typePillTypes[props.row.original.type.value],
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'number',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Numéro',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.number
                        ? formatPhoneNumber(props.row.original.number)
                        : 'Numéro masqué',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.name ?? '',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'tenantName',
            headerCustom: true,
            enableSorting: false,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Client',
                }),
            cell: (props) => {
                const row = props.row.original;

                if (row.tenant) {
                    return h(TextCell, { text: row.tenant.name });
                }

                const candidateNames = (row.candidateTenants ?? []).map(
                    (candidateTenant) =>
                        candidateTenant.isArchived
                            ? `${candidateTenant.name} - archivé`
                            : candidateTenant.name,
                );

                return h(TextCell, {
                    text:
                        candidateNames.length > 0
                            ? `Non rapproché (${candidateNames.join(', ')})`
                            : 'Non rapproché',
                    color: 'red',
                    length: 120,
                    title:
                        candidateNames.length > 0
                            ? `Clients possibles : ${candidateNames.join(', ')}`
                            : undefined,
                });
            },
        }),
        columnHelper.accessor(() => null, {
            id: 'duration',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Durée',
                    width: 0,
                }),
            cell: (props) =>
                h(TextCell, {
                    text: formatTime(
                        Math.floor(props.row.original.duration / 3600) || '',
                        String(
                            Math.floor(
                                (props.row.original.duration % 3600) / 60,
                            ),
                        ).padStart(2, '0'),
                    ),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'status',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Statut',
                    width: 0,
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue: props.row.original.status.label,
                    type: statusPillTypes[props.row.original.status.value],
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (cellProps) => {
                const row = cellProps.row.original;

                const onSuccess = () => {
                    toastSaved();

                    if (callback) {
                        callback();
                    }

                    updateData();
                };

                const isMissed = row.type.value === 'missed';

                return h(BaseCell, null, () => [
                    isMissed
                        ? null
                        : row.status.value === 'validated'
                          ? h(ActionButton, {
                                href: route(
                                    'front.projectManagement.apikom.updateAppointment',
                                    {
                                        tenantAppointment:
                                            row.tenantAppointmentId,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                size: 'small',
                                square: true,
                                title: 'Voir le rendez-vous',
                                type: 'primary',
                            })
                          : h(
                                ValidatePhoneCallAction,
                                {
                                    phoneCall: row,
                                    tenants: globalData.value
                                        ? globalData.value.tenants
                                        : [],
                                    users: globalData.value
                                        ? globalData.value.users
                                        : [],
                                    meansOfCommunications: globalData.value
                                        ? globalData.value.meansOfCommunications
                                        : [],
                                    callback: onSuccess,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            type: 'primary',
                                            square: true,
                                            title: row.tenant
                                                ? 'Créer le rendez-vous'
                                                : 'Associer un client et créer le rendez-vous',
                                            icon: LinkIcon,
                                        }),
                                },
                            ),
                    row.status.value === 'validated'
                        ? h(
                              InvalidatePhoneCallAction,
                              { phoneCall: row, callback: onSuccess },
                              {
                                  button: ({ action, loading }) =>
                                      h(ActionButton, {
                                          size: 'small',
                                          type: 'secondary',
                                          square: true,
                                          callback: () => action(),
                                          title: 'Remettre à traiter',
                                          icon: XIcon,
                                          loading,
                                      }),
                              },
                          )
                        : h(
                              TogglePhoneCallIgnoredAction,
                              { phoneCall: row, callback: onSuccess },
                              {
                                  button: ({ action, loading }) =>
                                      h(ActionButton, {
                                          size: 'small',
                                          type: 'secondary',
                                          square: true,
                                          callback: () => action(),
                                          title:
                                              row.status.value === 'ignored'
                                                  ? 'Remettre à traiter'
                                                  : 'Ignorer',
                                          icon:
                                              row.status.value === 'ignored'
                                                  ? ArrowUUpLeftIcon
                                                  : ProhibitIcon,
                                          loading,
                                      }),
                              },
                          ),
                ]);
            },
        }),
    ],
    url: route('front.projectManagement.apikom.postPhoneCalls'),
    data: {
        status,
        type,
        includeMissed,
    },
    selectionIdKey: 'id',
    columnVisibility: {
        duration: type !== 'missed',
    },
    columnPinning: {
        right: ['actions'],
    },
});

function refresh() {
    selectionIds.value = [];

    if (callback) {
        callback();
    }

    updateData();
}
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ImportPhoneCallsAction :callback="refresh">
                <template #button="{ action, loading: importing }">
                    <ActionButton
                        size="small"
                        :left-icon="ArrowsClockwiseIcon"
                        :loading="importing"
                        :callback="() => action()"
                    >
                        Récupérer les derniers appels
                    </ActionButton>
                </template>
            </ImportPhoneCallsAction>

            <IgnorePhoneCallsAction
                v-if="status === 'to_process'"
                :selection-ids="selectionIds"
                :count="
                    selectionIds.includes('all') ? count : selectionIds.length
                "
                :global-filter="globalFilter"
                :filters="filters"
                :data="{ status, type, includeMissed }"
                :callback="refresh"
            >
                <template #trigger>
                    <ActionButton
                        size="small"
                        type="secondary"
                        :left-icon="ProhibitIcon"
                        :disabled="selectionIds.length === 0"
                    >
                        Ignorer la sélection
                    </ActionButton>
                </template>
            </IgnorePhoneCallsAction>
        </template>
    </DataTableLayout>
</template>
